import React from 'react';
import { string } from 'prop-types';
import Decimal from 'decimal.js';

import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  propTypes,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_PROVIDER_COMMISSION,
  LINE_ITEM_HOST_INSURANCE,
  LINE_ITEM_HOST_INSURANCE_DISCOUNT,
} from '../../util/types';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

/**
 * Calculates the total price in sub units for multiple line items.
 */
// const lineItemsTotal = (lineItems, marketplaceCurrency) => {
//   console.log('lineItems :>> ', lineItems);
//   const amount = lineItems.filter(item => item.code != "line-item/Discount-5%-(6 horas o más)").reduce((total, item) => {
//     return total.plus(item.lineTotal.amount);
//   }, new Decimal(0));
//   console.log('amount :>> ', amount);
//   const currency = lineItems[0] ? lineItems[0].lineTotal.currency : marketplaceCurrency;
//   return new Money(amount, currency);
// };

const lineItemsTotal = (lineItems, marketplaceCurrency, isCustomer) => {
  const amount = lineItems.reduce((total, item) => {
    if (item.reversal) {
      return total;
    }
    if (isCommission(item)) {
      return total;
    }
    if (isCustomer && !(item.includeFor && item.includeFor.includes('customer'))) {
      return total;
    }
    if (!isCustomer && !(item.includeFor && item.includeFor.includes('provider'))) {
      return total;
    }
    return total.plus(item.lineTotal.amount);
  }, new Decimal(0));

  const currency = lineItems[0] ? lineItems[0].lineTotal.currency : marketplaceCurrency;
  return new Money(amount.toNumber(), currency);
};

/**
 * Checks if line item represents commission
 */
const isCommission = lineItem => {
  return (
    lineItem.code === LINE_ITEM_PROVIDER_COMMISSION ||
    lineItem.code === LINE_ITEM_CUSTOMER_COMMISSION
  );
};

/**
 * Checks if line item represents the host liability insurance (or its superhost discount)
 */
const isHostInsurance = lineItem => {
  return (
    lineItem.code === LINE_ITEM_HOST_INSURANCE ||
    lineItem.code === LINE_ITEM_HOST_INSURANCE_DISCOUNT
  );
};

/**
 * Returns the line items that make up the subtotal: everything except reversals,
 * commissions and the host insurance. Commissions and insurance are shown as their
 * own lines below the subtotal, so including them here would make them look like
 * they are subtracted twice.
 */
const getSubTotalLineItems = lineItems => {
  return lineItems.filter(item => !isCommission(item) && !isHostInsurance(item) && !item.reversal);
};

/**
 * Check if there is a commission line-item for the given user role.
 */
const hasCommission = (lineItems, userRole) => {
  let commissionLineItem = null;

  if (userRole === 'customer') {
    commissionLineItem = lineItems.find(item => item.code === LINE_ITEM_CUSTOMER_COMMISSION);
  } else if (userRole === 'provider') {
    commissionLineItem = lineItems.find(item => item.code === LINE_ITEM_PROVIDER_COMMISSION);
  }
  return !!commissionLineItem;
};

const LineItemSubTotalMaybe = props => {
  const { lineItems, code, userRole, intl, marketplaceCurrency, isCustomer } = props;

  // Customer fee details are intentionally hidden in UI. Showing subtotal without
  // commission would be misleading, so customers see only the final total.
  if (userRole === 'customer') {
    return null;
  }

  const refund = lineItems.find(item => item.code === code && item.reversal);

  // Show subtotal only if commission line-item is applicable to user or refund is issued.
  const showSubTotal = hasCommission(lineItems, userRole) || refund;

  // all non-reversal, non-commission, non-insurance line items
  const subTotalLineItems = getSubTotalLineItems(lineItems);
  // line totals of those line items combined
  const subTotal = lineItemsTotal(subTotalLineItems, marketplaceCurrency, isCustomer);
  const formattedSubTotal = subTotalLineItems.length > 0 ? formatMoney(intl, subTotal) : null;

  return formattedSubTotal && showSubTotal ? (
    <>
      <hr className={css.totalDivider} />
      <div className={css.subTotalLineItem}>
        <span className={css.itemLabel}>
          <FormattedMessage id="OrderBreakdown.subTotal" />
        </span>
        <span className={css.itemValue}>{formattedSubTotal}</span>
      </div>
    </>
  ) : null;
};

LineItemSubTotalMaybe.propTypes = {
  lineItems: propTypes.lineItems.isRequired,
  userRole: string.isRequired,
  intl: intlShape.isRequired,
};

export default LineItemSubTotalMaybe;
